import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { parse } from 'query-string';
import React, { FC, useContext, useEffect, useState } from 'react';

import PhoneNumberEnrollmentFormPage from '@ui/routes/SignIn/PhoneNumberEnrollmentForm';
import { UnverifiedSMSCodeInputForm } from '@ui/routes/SignIn/SMSCodeInputForm';
import TOTPCodeInputFormPage from '@ui/routes/SignIn/TOTPCodeInputForm';
import awsAuthErrorMessages from '@ui/routes/SignIn/awsAuthErrorMessages';

import { deleteDataStore } from '@utils/indexedDb';
import { mapKeys } from '@utils/mapKeys';

import { AuthStep, AuthStepContext, useAuth } from '@providers/AuthProvider';
import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import MembershipSetPasswordStep from '../MembershipUserSetPasswordStep';

interface IMembershipAuthStepProps extends IFlowStepProps {
	stepKey: string;
	email?: string;
	registrationCode: string;
}

const MembershipAuthStep: FC<IMembershipAuthStepProps> = ({ email, registrationCode, stepKey }) => {
	const [phoneNumber, setPhoneNumber] = useState('');
	const [notify, setNotify] = useState<boolean | 'true' | null | undefined>(null);
	const [credentials, setCredentials] = useState({ email: '', password: '' });
	const [isSmsMfa, setIsSmsMfa] = useState(false);
	const [authComplete, setAuthComplete] = useState(false);
	const location = useLocation();

	const { destinationFlow, existingFormData, handleNavBack, handleNavNext, submitFormData } =
		useContext(FlowContext);
	const { signIn, setUpSMSMultifactor, userId } = useAuth();

	const [authStep, setAuthStep] = useState<AuthStepContext>({
		nextStep: AuthStep.needPassword,
	});

	const submitOnboardingData = async () => {
		const mfaData = phoneNumber ? { phone: phoneNumber, notify, smsMfaEnrolled: true } : {};

		if (existingFormData?.email) {
			await submitFormData({ ...mfaData });
			return;
		} else {
			await submitFormData({ email, ...mfaData });
			return;
		}
	};

	const removeOnboardingData = async () => {
		await deleteDataStore('user');
	};

	const completeZendeskOnboarding = async () => {
		await navigate(`/app/zendesk_sso?return_to=${process.env.GATSBY_SUPPORT_LINK}`);
		return;
	};

	const completeOnboarding = async () => {
		const { support_register } = mapKeys((s) => s, parse(location.search, { parseNumbers: true }));
		if (support_register === 'zendesk') {
			await completeZendeskOnboarding();
			await removeOnboardingData();
			return;
		}

		await submitOnboardingData();
		await removeOnboardingData();
		handleNavNext(stepKey);
	};

	const startMFAEnrollment = async () => {
		const newAuthStep = await signIn(credentials.email, credentials.password);

		setAuthStep(newAuthStep as AuthStepContext);
	};

	useEffect(() => {
		if (isSmsMfa && credentials.email && credentials.password) {
			startMFAEnrollment();
		}
	}, [credentials.email, credentials.password, isSmsMfa]);

	useEffect(() => {
		if (userId && authComplete) {
			completeOnboarding();
		}
	}, [userId, authComplete]);

	const resendCode = async () => {
		try {
			const newAuthStep = await setUpSMSMultifactor(authStep?.user, phoneNumber ?? '');

			setAuthStep({ ...authStep, user: newAuthStep?.user } as AuthStepContext);
		} catch (e: any) {
			if (
				e?.message === awsAuthErrorMessages.expiredSession.awsErrorMessage ||
				e?.message === awsAuthErrorMessages.invalidSession.awsErrorMessage
			) {
				setAuthStep({ nextStep: AuthStep.needPassword });
			}
		}
	};

	const renderAuthStep = (currentStep: AuthStep) => {
		switch (currentStep) {
			case AuthStep.needPassword:
				return (
					<MembershipSetPasswordStep
						registrationCode={registrationCode}
						stepKey={stepKey}
						setIsSmsMfa={setIsSmsMfa}
						setCredentials={setCredentials}
						onComplete={() => setAuthComplete(true)}
						destinationFlow={destinationFlow}
						handleNavBack={handleNavBack}
					/>
				);

			case AuthStep.needSMSMultifactorSetupPhone:
				return (
					<PhoneNumberEnrollmentFormPage
						isSignUp={true}
						authStep={authStep}
						setAuthStep={setAuthStep}
						setPhoneNumber={setPhoneNumber}
						setNotify={setNotify}
					/>
				);

			case AuthStep.needSMSMultifactorSetupVerification:
				return (
					<UnverifiedSMSCodeInputForm
						authStep={authStep}
						onComplete={() => setAuthComplete(true)}
						setAuthStep={setAuthStep}
						phoneNumber={phoneNumber}
						resendCode={resendCode}
					/>
				);
			case AuthStep.needTOTPMultifactorSetup:
			case AuthStep.needTOTPMultifactorVerification:
				return (
					<TOTPCodeInputFormPage
						authStep={authStep}
						onComplete={() => setAuthComplete(true)}
						setAuthStep={setAuthStep}
					/>
				);
			default:
				return <div />;
		}
	};

	return renderAuthStep(authStep.nextStep as AuthStep);
};

export default MembershipAuthStep;
